import React, { useState } from 'react';
import { useClient } from '../../contexts/ClientContext';
import { useToast } from '../../hooks/use-toast';
import { getApiUrl } from '../../utils/config';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Mail, UserPlus, Loader2, Shield, Edit3, User, Eye } from 'lucide-react';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { currentClient } = useClient();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    role: 'member',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentClient) return;

    try {
      setLoading(true);
      const response = await fetch(`${getApiUrl()}/api/v1/clients/${currentClient.id}/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: "Invitation Sent",
          description: `Invitation sent to ${formData.email}`,
        });
        onSuccess();
        onClose();
        setFormData({ email: '', role: 'member', message: '' });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send invitation');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3 text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent dark:from-indigo-400 dark:to-purple-400">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-white" />
            </div>
            <span>Invite Team Member</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Email Address
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <Input
                id="email"
                type="email"
                placeholder="colleague@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10 bg-white/50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 focus:border-indigo-500 dark:focus:border-indigo-400 transition-colors"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Role
            </Label>
            <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
              <SelectTrigger className="bg-white/50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 focus:border-indigo-500 dark:focus:border-indigo-400">
                <div className="flex items-start space-x-2 w-full">
                  {formData.role === 'admin' && (
                    <>
                      <Shield className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">Admin</div>
                        <div className="text-xs text-slate-500">Full access except user management</div>
                      </div>
                    </>
                  )}
                  {formData.role === 'editor' && (
                    <>
                      <Edit3 className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">Editor</div>
                        <div className="text-xs text-slate-500">Can create and edit content</div>
                      </div>
                    </>
                  )}
                  {formData.role === 'member' && (
                    <>
                      <User className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">Member</div>
                        <div className="text-xs text-slate-500">Can create content</div>
                      </div>
                    </>
                  )}
                </div>
              </SelectTrigger>
              <SelectContent className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40">
                <SelectItem value="admin" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/20">
                  <div className="flex items-start space-x-2">
                    <Shield className="w-4 h-4 text-red-500 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium">Admin</div>
                      <div className="text-xs text-slate-500">Full access except user management</div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="editor" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/20">
                  <div className="flex items-start space-x-2">
                    <Edit3 className="w-4 h-4 text-blue-500 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium">Editor</div>
                      <div className="text-xs text-slate-500">Can create and edit content</div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="member" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/20">
                  <div className="flex items-start space-x-2">
                    <User className="w-4 h-4 text-green-500 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium">Member</div>
                      <div className="text-xs text-slate-500">Can create content</div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="viewer" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/20">
                  <div className="flex items-center space-x-2">
                    <Eye className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="font-medium">Viewer</div>
                      <div className="text-xs text-slate-500">Read-only access</div>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Personal Message (Optional)
            </Label>
            <Textarea
              id="message"
              placeholder="Add a personal message to the invitation..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={3}
              className="bg-white/50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 focus:border-indigo-500 dark:focus:border-indigo-400 transition-colors resize-none"
            />
          </div>

          <DialogFooter className="gap-3">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white border-0 shadow-lg shadow-indigo-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/30"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};